import { type ReactNode, type ToggleEventHandler, useMemo } from 'react';

import { isClientHydrated } from '#/rendering/state/renderingState';
import { richDetailsAttributes } from '#/snippets/rich-details.attributes';
import {
  trackClickedAttributes,
  type TrackClickedData,
} from '#/snippets/track-clicked.attributes';

import { richDetailsStateClosedClass } from './richDetailsClasses';

type Props = {
  id?: string;
  name?: string;
  children: ReactNode;
  className?: string;
  /**
   * If marked as singleton, the details will close whenever another singleton is opened
   */
  singleton?: boolean;
  qaId?: string;
  desktopTriggersOnHover?: boolean;
  trackClickedOpen?: TrackClickedData | null;
  defaultOpen?: boolean;
  onToggle?: ToggleEventHandler<HTMLDetailsElement>;
};

export function RichDetails({
  id,
  name,
  children,
  className,
  qaId,
  desktopTriggersOnHover,
  singleton,
  trackClickedOpen,
  defaultOpen,
  onToggle,
}: Props) {
  // We only set this class manually once, make sure it is also present for lazy-loaded
  // elements and elements created after client-side navigation
  const initialClassName = useMemo(
    () => (isClientHydrated() ? richDetailsStateClosedClass : undefined),
    [],
  );

  return (
    <details
      id={id}
      name={name}
      className={[className, initialClassName].filter(Boolean).join(' ')}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...richDetailsAttributes({ desktopTriggersOnHover, singleton })}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(trackClickedOpen
        ? trackClickedAttributes(trackClickedOpen)
        : undefined)}
      suppressHydrationWarning
      open={defaultOpen}
      onToggle={onToggle}
      data-qa-id={qaId}
    >
      {children}
    </details>
  );
}
